import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '/api';
const statusColor = s => ({ pending: '#FFB800', processing: '#FF6B00', shipped: '#2196F3', delivered: '#4CAF50', cancelled: '#C0392B' }[s] || '#888');

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [trackNum, setTrackNum] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const stored = localStorage.getItem('customer_data');
    if (!token) { navigate('/account'); return; }
    if (stored) { try { setCustomer(JSON.parse(stored)); } catch {} }

    fetch(`${API}/get-customer-orders.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setOrders(d.orders || []))
      .catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);

  const logout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    // Dispatch storage event so Header updates immediately (same-tab)
    window.dispatchEvent(new Event('storage'));
    navigate('/account');
  };

  const trackOrder = async () => {
    if (!trackNum.trim()) return;
    setTrackLoading(true); setTrackResult(null);
    const token = localStorage.getItem('customer_token');
    const r = await fetch(`${API}/track-order.php?order_id=${encodeURIComponent(trackNum.trim())}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).catch(() => null);
    setTrackLoading(false);
    setTrackResult(r);
  };

  const STEPS = ['Order Placed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];
  const stepIdx = (s) => ({ pending: 0, processing: 1, shipped: 2, out_for_delivery: 3, delivered: 4 }[s] ?? -1);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 'clamp(1.3rem,3vw,1.8rem)', background: 'linear-gradient(135deg,#FFE082,#FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '.3rem' }}>My Account</h1>
          <p style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', color: 'rgba(255,224,130,.5)', fontStyle: 'italic' }}>
            Welcome back, {customer?.name || 'Devotee'} 🙏
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.8rem' }}>
          <Link to="/shop" style={{ padding: '.6rem 1.2rem', background: 'rgba(255,184,0,.1)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 6, fontFamily: "'Cinzel',serif", fontSize: '.68rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,224,130,.7)', textDecoration: 'none' }}>🛍 Shop</Link>
          <button onClick={logout} style={{ padding: '.6rem 1.2rem', background: 'rgba(192,57,43,.12)', border: '1px solid rgba(192,57,43,.25)', borderRadius: 6, fontFamily: "'Cinzel',serif", fontSize: '.68rem', letterSpacing: '.1em', textTransform: 'uppercase', color: '#ff6b6b', cursor: 'pointer' }}>Logout</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Total Orders', val: orders.length, icon: '📦', color: '#FFB800' },
          { label: 'Delivered', val: orders.filter(o => o.status === 'delivered').length, icon: '✅', color: '#4CAF50' },
          { label: 'In Transit', val: orders.filter(o => ['processing', 'shipped'].includes(o.status)).length, icon: '🚚', color: '#2196F3' },
          { label: 'Total Spent', val: `₹${orders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0).toLocaleString('en-IN')}`, icon: '💰', color: '#FF6B00' },
        ].map(c => (
          <div key={c.label} style={{ background: 'linear-gradient(135deg,rgba(255,107,0,.07),rgba(36,0,70,.25))', border: '1px solid rgba(255,184,0,.15)', borderRadius: 8, padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '.4rem' }}>{c.icon}</div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.1rem', color: c.color }}>{c.val}</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,224,130,.45)', marginTop: '.3rem' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,184,0,.15)', marginBottom: '1.5rem', gap: '.2rem' }}>
        {[{ id: 'orders', label: '📦 My Orders' }, { id: 'track', label: '🔍 Track Order' }, { id: 'profile', label: '👤 Profile' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '.75rem 1.3rem', fontFamily: "'Cinzel',serif", fontSize: '.7rem', letterSpacing: '.12em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', color: activeTab === t.id ? '#FFB800' : 'rgba(255,224,130,.4)', borderBottom: activeTab === t.id ? '2px solid #FF6B00' : '2px solid transparent', transition: 'all .2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 360px' : '1fr', gap: '1.5rem' }}>
          <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.95),rgba(26,10,46,.95))', border: '1px solid rgba(255,184,0,.18)', borderRadius: 10, overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif" }}>🌀 Loading orders…</div>
            ) : orders.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🪔</div>
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.8rem', letterSpacing: '.12em', color: 'rgba(255,224,130,.4)', marginBottom: '1.2rem' }}>No orders yet</div>
                <Link to="/shop" style={{ padding: '.75rem 1.8rem', background: 'linear-gradient(135deg,#FF6B00,#FFB800)', border: 'none', borderRadius: 6, color: '#0D0500', fontFamily: "'Cinzel',serif", fontSize: '.75rem', fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', textDecoration: 'none' }}>Start Shopping 🛍</Link>
              </div>
            ) : orders.map(order => (
              <div key={order.id} onClick={() => setSelected(order)} style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid rgba(255,184,0,.08)', cursor: 'pointer', background: selected?.id === order.id ? 'rgba(255,184,0,.05)' : 'transparent', transition: 'background .2s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem' }}>
                <div>
                  <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.8rem', color: '#FFB800', fontWeight: 700 }}>Order #{order.id}</span>
                  <div style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.8rem', color: 'rgba(255,224,130,.45)', marginTop: '.2rem' }}>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.9rem', color: '#FFE082', fontWeight: 700 }}>₹{Number(order.total_amount).toLocaleString('en-IN')}</span>
                  <span style={{ background: `${statusColor(order.status)}18`, color: statusColor(order.status), border: `1px solid ${statusColor(order.status)}44`, borderRadius: 20, padding: '.2rem .8rem', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>{order.status}</span>
                </div>
              </div>
            ))}
          </div>

          {selected && (
            <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.98),rgba(26,10,46,.98))', border: '1px solid rgba(255,184,0,.25)', borderRadius: 10, padding: '1.5rem', height: 'fit-content', position: 'sticky', top: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: '.78rem', letterSpacing: '.18em', textTransform: 'uppercase', color: '#FFB800' }}>Order #{selected.id}</h3>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: '1px solid rgba(255,184,0,.25)', color: '#FFE082', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem' }}>✕</button>
              </div>

              {/* Tracking Steps */}
              <div style={{ marginBottom: '1.5rem' }}>
                {STEPS.map((step, i) => {
                  const current = stepIdx(selected.status);
                  const done = i <= current;
                  return (
                    <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.4rem 0' }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: done ? 'linear-gradient(135deg,#FF6B00,#FFB800)' : 'rgba(255,255,255,.08)', border: done ? 'none' : '1px solid rgba(255,184,0,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '.7rem', color: done ? '#0D0500' : 'rgba(255,224,130,.3)', fontWeight: 700 }}>{done ? '✓' : i + 1}</div>
                      <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.7rem', letterSpacing: '.08em', color: done ? '#FFE082' : 'rgba(255,224,130,.3)' }}>{step}</span>
                    </div>
                  );
                })}
              </div>

              {selected.tracking_number && (
                <div style={{ padding: '.75rem 1rem', background: 'rgba(33,150,243,.1)', border: '1px solid rgba(33,150,243,.25)', borderRadius: 6, marginBottom: '1rem' }}>
                  <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.15em', textTransform: 'uppercase', color: '#2196F3', marginBottom: '.3rem' }}>Tracking Number</div>
                  <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.85rem', color: '#FFE082', fontWeight: 700 }}>{selected.tracking_number}</div>
                </div>
              )}

              <div style={{ padding: '.75rem 1rem', background: 'rgba(255,184,0,.05)', border: '1px solid rgba(255,184,0,.1)', borderRadius: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Cinzel',serif", fontSize: '.78rem' }}>
                  <span style={{ color: 'rgba(255,224,130,.6)' }}>Total</span>
                  <span style={{ color: '#FFB800', fontWeight: 700 }}>₹{Number(selected.total_amount).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Track Order Tab */}
      {activeTab === 'track' && (
        <div style={{ maxWidth: 500 }}>
          <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.98),rgba(26,10,46,.98))', border: '1px solid rgba(255,184,0,.22)', borderRadius: 10, padding: '2rem' }}>
            <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: '.78rem', letterSpacing: '.2em', textTransform: 'uppercase', color: '#FFB800', marginBottom: '1.2rem' }}>🔍 Track Your Order</h3>
            <div style={{ display: 'flex', gap: '.7rem' }}>
              <input value={trackNum} onChange={e => setTrackNum(e.target.value)} placeholder="Enter Order # (e.g. 1042)"
                style={{ flex: 1, padding: '.85rem 1rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 6, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '1rem', outline: 'none' }} />
              <button onClick={trackOrder} disabled={trackLoading}
                style={{ padding: '.85rem 1.4rem', background: 'linear-gradient(135deg,#FF6B00,#FFB800)', border: 'none', borderRadius: 6, color: '#0D0500', fontFamily: "'Cinzel',serif", fontSize: '.75rem', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', cursor: trackLoading ? 'not-allowed' : 'pointer' }}>
                {trackLoading ? '…' : 'Track'}
              </button>
            </div>
            {trackResult && (
              <div style={{ marginTop: '1.5rem', padding: '1.2rem', background: 'rgba(255,184,0,.05)', border: '1px solid rgba(255,184,0,.15)', borderRadius: 8 }}>
                {trackResult.success ? (
                  <>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.72rem', letterSpacing: '.12em', textTransform: 'uppercase', color: '#FF6B00', marginBottom: '.5rem' }}>Order #{trackResult.order?.id}</div>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.9rem', color: '#FFE082', marginBottom: '.5rem' }}>Status: <span style={{ color: statusColor(trackResult.order?.status) }}>{trackResult.order?.status}</span></div>
                    {trackResult.order?.tracking_number && <div style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.85rem', color: 'rgba(255,224,130,.6)' }}>Tracking #: {trackResult.order.tracking_number}</div>}
                  </>
                ) : <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.75rem', color: '#ff6b6b' }}>❌ {trackResult.message || 'Order not found'}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && customer && (
        <div style={{ maxWidth: 500 }}>
          <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.98),rgba(26,10,46,.98))', border: '1px solid rgba(255,184,0,.22)', borderRadius: 10, padding: '2rem' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#FF6B00,#FFB800)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0D0500', fontWeight: 700, fontSize: '1.6rem', marginBottom: '1.2rem' }}>
              {customer.name?.charAt(0) || '?'}
            </div>
            {[['Name', customer.name], ['Email', customer.email], ['Phone', customer.phone || '—']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '.7rem 0', borderBottom: '1px solid rgba(255,184,0,.08)' }}>
                <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.65rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(255,184,0,.5)' }}>{k}</span>
                <span style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', color: '#FFE082' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
